import { useContext, useState, useMemo, useRef, useEffect } from 'react';
import { Button, Modal, Image } from "react-bootstrap";
import { SmartContext } from '../../../library/Core/SmartContext';
import { axiosClient } from "../../../library/Service/axiosClient";
import { ACTIVITY_CATEGORIES } from './activityCategories';

function AddNewFormModal({ className, isSuperAdmin, preselectedInstituteId, instituteDomain }) {
  const { state } = useContext(SmartContext);
  const [show, setShow] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedAlumni, setSelectedAlumni] = useState(null);

  const [activityDate, setActivityDate] = useState("");
  const [category, setCategory] = useState("");
  const [mode, setMode] = useState("");
  const [description, setDescription] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Institute selector state (SUPER_ADMIN only)
  const [selectedInstituteId, setSelectedInstituteId] = useState(preselectedInstituteId || null);
  const [selectedInstituteName, setSelectedInstituteName] = useState(() => {
    if (!preselectedInstituteId || !Array.isArray(instituteDomain)) return '';
    const match = instituteDomain.find(inst => String(inst.code) === String(preselectedInstituteId));
    return match?.value || '';
  });
  const [instituteSearchText, setInstituteSearchText] = useState('');
  const [showInstituteDropdown, setShowInstituteDropdown] = useState(false);

  const debounceRef = useRef(null);

  // Sync institute selection when dashboard's selectedInstituteId changes
  useEffect(() => {
    if (!isSuperAdmin) return;
    setSelectedInstituteId(preselectedInstituteId || null);
    if (preselectedInstituteId && Array.isArray(instituteDomain)) {
      const match = instituteDomain.find(inst => String(inst.code) === String(preselectedInstituteId));
      setSelectedInstituteName(match?.value || '');
    } else {
      setSelectedInstituteName('');
    }
    setSelectedAlumni(null);
    setSearchText('');
    setSearchResults([]);
  }, [preselectedInstituteId]);

  const filteredInstitutes = useMemo(() => {
    if (!instituteDomain || !Array.isArray(instituteDomain)) return [];
    if (!instituteSearchText.trim()) return instituteDomain.slice(0, 50);
    const q = instituteSearchText.toLowerCase();
    return instituteDomain.filter(inst => inst.value?.toLowerCase().includes(q)).slice(0, 50);
  }, [instituteDomain, instituteSearchText]);

  const resolvedInstituteId = useMemo(() => {
    if (isSuperAdmin) return selectedInstituteId || null;
    const institute_id_student = state?.data?.selectedInstitute?.instituteId;
    const user = sessionStorage.getItem('user');
    const parsedUser = user ? JSON.parse(user) : null;
    return institute_id_student || parsedUser?.primaryInstitute || null;
  }, [state, isSuperAdmin, selectedInstituteId]);

  const handleShow = () => setShow(true);

  const handleClose = () => {
    setShow(false);
    setSearchText("");
    setSearchResults([]);
    setSelectedAlumni(null);
    setActivityDate("");
    setCategory("");
    setMode("");
    setDescription("");
    setErrors({});
    if (isSuperAdmin && !preselectedInstituteId) {
      setSelectedInstituteId(null);
      setSelectedInstituteName('');
      setInstituteSearchText('');
    }
  };

  // Read uploader info from sessionStorage
  const userDataString = sessionStorage.getItem("user");
  let added_by_user_id = null;
  let added_by_name = null;

  if (userDataString) {
    const u = JSON.parse(userDataString);
    added_by_user_id = u.userId || null;
    added_by_name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || null;
  }

  const doSearch = async (value) => {
    if (!value) {
      setSearchResults([]);
      return;
    }
    setIsLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const schoolId = isSuperAdmin && selectedInstituteId
        ? [selectedInstituteId]
        : (user.institutes ?? [user.instituteId]);

      const criteria = {
        context: { searchBy: "default", level: "MY_SCHOOL" },
        name: value,
        passOutYear: [],
        educationOrEmploymentCurrentStatusCode: [],
        fieldOfStudyCode: [],
        universityId: [],
        pgUniversityId: [],
        collegeCountryCode: [],
        collegeCityCode: [],
        residingCountryCode: [],
        residingCityCode: [],
        entranceTestCode: [],
        skills: [],
        interests: [],
        programId: [],
        jobCompanyId: [],
        functionalAreaCode: [],
        industryCode: [],
        workingStatusCode: [],
        typeOfPracticeCode: [],
        jobRoleCode: [],
        defenceServiceBranchCode: [],
        seniorityLevelCode: [],
        higherStudyAtUG: null,
        schoolId: schoolId,
        ugCollegeExists: true,
        userTypeCode: "ALUMNI",
      };

      const url = `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/search/all-alumni-list`;
      const response = await axiosClient().post(url, criteria);

      setSearchResults(response.data || []);
    } catch (err) {
      console.error("Search API error:", err);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleSelect = (alumni) => {
    setSelectedAlumni(alumni);
    setSearchText(alumni.nAME);
    setSearchResults([]);
    setErrors((prev) => ({ ...prev, searchText: "" }));
  };

  const validate = () => {
    const temp = {};

    if (isSuperAdmin && !selectedInstituteId) temp.selectedInstituteId = 'Please select an institute first';
    if (!selectedAlumni) temp.searchText = "Please select an alumni";
    if (!activityDate) temp.activityDate = "Activity Date is required";
    if (!category) temp.category = "Category is required";
    if (!mode) temp.mode = "Mode is required";

    const desc = (description || "").trim();
    if (!desc) {
      temp.description = "Description is required";
    } else if (desc.length < 50) {
      temp.description = "Description must be at least 50 characters";
    } else if (desc.length > 250) {
      temp.description = "Description must not exceed 250 characters";
    }

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        uuid: selectedAlumni.uuid,
        activityDate,
        category,
        mode,
        description: description.trim(),
        added_by_user_id,
        added_by_name,
        institute_id: resolvedInstituteId,
      };

      const requestUrl = `${process.env.REACT_APP_BROADCAST_SERVER}/activity-log/add-single-data`;
      const response = await axiosClient().post(requestUrl, payload);

      console.log("Submit Success:", response.data);
      alert("Activity saved successfully!");
      handleClose();
    } catch (err) {
      console.error("Submit API error:", err);
      alert("Failed to save activity. Please try again.");
    }
  };

  return (
    <>
      <Button className={className} onClick={handleShow}>
        <i className="icon-plus position-relative"></i>
        <span>Add New</span>
      </Button>

      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header className="bg-gray-f2 border-0" closeButton>
          <Modal.Title>Add New Activity Entry</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          <form className="needs-validation" noValidate onSubmit={handleSubmit}>
            <div className="row g-3">
              {/* INSTITUTE SELECTOR — SUPER_ADMIN only */}
              {isSuperAdmin && (
                <div className="col-md-12 position-relative">
                  <label className="form-label mb-1">Institute <span className="text-danger">*</span></label>
                  {selectedInstituteId ? (
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={selectedInstituteName}
                        readOnly
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setSelectedInstituteId(null);
                          setSelectedInstituteName('');
                          setInstituteSearchText('');
                          setSelectedAlumni(null);
                          setSearchText('');
                          setSearchResults([]);
                        }}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        className={`form-control${errors.selectedInstituteId ? ' is-invalid' : ''}`}
                        placeholder="Search institute..."
                        value={instituteSearchText}
                        onChange={(e) => {
                          setInstituteSearchText(e.target.value);
                          setShowInstituteDropdown(true);
                        }}
                        onFocus={() => setShowInstituteDropdown(true)}
                        onBlur={() => setTimeout(() => setShowInstituteDropdown(false), 200)}
                        autoComplete="off"
                      />
                      {errors.selectedInstituteId && (
                        <div className="invalid-feedback">{errors.selectedInstituteId}</div>
                      )}
                      {showInstituteDropdown && filteredInstitutes.length > 0 && (
                        <div
                          className="position-absolute w-100 bg-white border rounded shadow-sm"
                          style={{ zIndex: 20, maxHeight: '220px', overflowY: 'auto', top: '100%', left: 0 }}
                        >
                          {filteredInstitutes.map((inst) => (
                            <div
                              key={inst.code || inst.value}
                              className="p-2 cursor-pointer hover-bg-light"
                              style={{ cursor: 'pointer' }}
                              onMouseDown={() => {
                                setSelectedInstituteId(inst.code || inst.value);
                                setSelectedInstituteName(inst.value);
                                setInstituteSearchText('');
                                setShowInstituteDropdown(false);
                                setErrors((prev) => ({ ...prev, selectedInstituteId: '' }));
                              }}
                            >
                              {inst.value}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* SEARCH BOX */}
              <div className="col-md-12 position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder={isSuperAdmin && !selectedInstituteId ? "Select an institute first" : "Search by alumni name"}
                  value={searchText}
                  onChange={handleInputChange}
                  disabled={isSuperAdmin && !selectedInstituteId}
                  autoComplete="off"
                />
                {errors.searchText ? (
                  <div className="text-danger mt-1">{errors.searchText}</div>
                ) : null}

                {searchText && searchResults.length > 0 && (
                  <div
                    className="position-absolute w-100 bg-white border rounded shadow-sm"
                    style={{ zIndex: 10, maxHeight: "250px", overflowY: "auto" }}
                  >
                    {isLoading ? (
                      <div className="p-2 text-center text-secondary">Loading...</div>
                    ) : (
                      searchResults.map((alumni) => (
                        <div
                          key={alumni.uuid}
                          className="d-flex align-items-center p-2 hover-bg-light cursor-pointer"
                          onClick={() => handleSelect(alumni)}
                        >
                          {alumni.profilePictureFilePath ? (
                            <Image
                              src={alumni.profilePictureFilePath}
                              roundedCircle
                              width={30}
                              height={30}
                              className="me-2"
                            />
                          ) : null}
                          <div>
                            <div>{alumni.nAME}</div>
                            <small className="text-secondary">{`UUID: ${alumni.uuid}`}</small>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* SELECTED ALUMNI INFO */}
              {selectedAlumni ? (
                <div className="col-md-12">
                  <div className="bg-gray-f2 border rounded p-4">
                    <div className="row g-3 align-items-center">
                      {selectedAlumni.profilePictureFilePath ? (
                        <div className="col-auto">
                          <Image
                            src={selectedAlumni.profilePictureFilePath}
                            roundedCircle
                            width={80}
                            height={80}
                          />
                        </div>
                      ) : null}
                      <div className="col">
                        <h5>{selectedAlumni.nAME}</h5>
                        <p className="text-secondary mb-0">{`UUID: ${selectedAlumni.uuid}`}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* ACTIVITY DATE */}
              <div className="col-md-4">
                <label className="form-label mb-1">Activity Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={activityDate}
                  onChange={(e) => {
                    setActivityDate(e.target.value);
                    setErrors((prev) => ({ ...prev, activityDate: "" }));
                  }}
                />
                {errors.activityDate ? (
                  <div className="text-danger mt-1">{errors.activityDate}</div>
                ) : null}
              </div>

              {/* CATEGORY */}
              <div className="col-md-4">
                <label className="form-label mb-1">Category</label>
                <select
                  className="form-select"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setErrors((prev) => ({ ...prev, category: "" }));
                  }}
                >
                  <option value="">Select Category</option>
                  {ACTIVITY_CATEGORIES.map(({ code, label }) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
                {errors.category ? (
                  <div className="text-danger mt-1">{errors.category}</div>
                ) : null}
              </div>

              {/* MODE */}
              <div className="col-md-4">
                <label className="form-label mb-1">Mode</label>
                <select
                  className="form-select"
                  value={mode}
                  onChange={(e) => {
                    setMode(e.target.value);
                    setErrors((prev) => ({ ...prev, mode: "" }));
                  }}
                >
                  <option value="">Select Mode</option>
                  <option value="EMAIL">Email</option>
                  <option value="WHATSAPP">WhatsApp</option>
                  <option value="PHONE_CALL">Phone Call</option>
                  <option value="CAMPUS_VISIT">On-Campus Visit</option>
                  <option value="PHYSICAL_EVENT">Physical Event</option>
                  <option value="ONLINE_EVENT">Online Event</option>
                  <option value="OTHERS">Others</option>
                </select>
                {errors.mode ? <div className="text-danger mt-1">{errors.mode}</div> : null}
              </div>

              {/* DESCRIPTION */}
              <div className="col-sm-12">
                <label className="form-label mb-1">Description</label>
                <textarea
                  className="form-control"
                  rows="8"
                  placeholder="Description (50–250 characters)"
                  value={description}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDescription(value);

                    const trimmed = value.trim();
                    if (!trimmed) {
                      setErrors((prev) => ({ ...prev, description: "Description is required" }));
                    } else if (trimmed.length < 50) {
                      setErrors((prev) => ({ ...prev, description: "Description must be at least 50 characters" }));
                    } else if (trimmed.length > 250) {
                      setErrors((prev) => ({ ...prev, description: "Description must not exceed 250 characters" }));
                    } else {
                      setErrors((prev) => ({ ...prev, description: "" }));
                    }
                  }}
                />

                <div className="d-flex justify-content-between mt-1">
                  <div className="text-danger">{errors.description || ""}</div>
                  <small className={description.trim().length > 250 ? "text-danger" : "text-muted"}>
                    {description.trim().length}/250
                  </small>
                </div>
              </div>

              {/* SAVE BUTTON */}
              <div className="col-sm-12 text-center">
                <button type="submit" className="btn btn-green mx-auto max-135">
                  <span>Save</span>
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AddNewFormModal;
